import React, {useState, useEffect, useLayoutEffect, useRef} from 'react'
import {Box, Button} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

function getIOSVersion() {
    const ua = navigator.userAgent
    const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1

    if (/iPhone|iPad|iPod/.test(ua) || isIPadOS) {
        const match = ua.match(/OS X (\d+)_?(\d+)?_?(\d+)?/)
        if (match) {
            return {
                major: parseInt(match[1], 10),
                minor: parseInt(match[2] || '0', 10),
                patch: parseInt(match[3] || '0', 10)
            }
        }

        return null
    }

    return null
}

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

export const PageFooter = ({category}) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [isTruncated, setIsTruncated] = useState(false)
    const [isIOS15, setIsIOS15] = useState(false)
    const textRef = useRef(null)
    const intl = useIntl()

    useEffect(() => {
        setIsExpanded(false)
    }, [category?.c_bottomCategoryDescription])

    useIsomorphicLayoutEffect(() => {
        if (textRef.current) {
            const lineHeight = parseInt(window.getComputedStyle(textRef.current).lineHeight, 10)
            const maxHeight = lineHeight * 3
            setIsTruncated(textRef.current.scrollHeight > maxHeight)
        }
    }, [category?.c_bottomCategoryDescription, isIOS15])

    useEffect(() => {
        const validation = (() => {
            if (typeof navigator === 'undefined') return false

            const version = getIOSVersion()
            if (version && version?.major === 10 && version?.minor === 15) {
                return true
            }

            return false
        })()

        setIsIOS15(validation)
    }, [])

    return (
        <Box
            width="100%"
            overflow="hidden"
            padding={{
                base: '0px 15px 30px 15px',
                md: '0px 60px 30px 60px',
                lg: '0px 60px 30px 60px',
                xl: '0px 180px 30px 180px'
            }}
            marginTop={{base: '15px', md: '30px'}}
        >
            <Box borderColor="gray.600 !important" borderTop="1px solid">
                {category?.c_bottomCategoryHeading && (
                    <Box
                        fontSize="25px"
                        fontWeight="400"
                        lineHeight="35px"
                        marginRight={2}
                        margin="20px 0"
                        color="black.100"
                        fontFamily="heading"
                        sx={{
                            '& p': {
                                color: 'black.100',
                                lineHeight: '35px',
                                fontWeight: '400',
                                fontFamily: 'heading',
                                fontSize: '25px'
                            },
                            '& span': {
                                color: 'black.100',
                                lineHeight: '35px',
                                fontWeight: '400',
                                fontFamily: 'heading',
                                fontSize: '25px'
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: category?.c_bottomCategoryHeading}}
                    />
                )}
                {category?.c_bottomCategoryDescription && (
                    <>
                        <Box
                            fontSize="16px"
                            lineHeight="25px"
                            fontFamily="body"
                            color="black.100"
                            fontWeight="300"
                            sx={{
                                p: {
                                    color: 'black.100',
                                    margin: '0px 0px 5px 0px',
                                    lineHeight: '25px',
                                    fontWeight: '300',
                                    fontFamily: 'body',
                                    fontSize: '16px'
                                },
                                a: {
                                    color: 'black.100',
                                    textDecoration: 'underline',
                                    lineHeight: '25px',
                                    fontSize: '16px',
                                    fontWeight: '300',
                                    fontFamily: 'body',
                                    _hover: {
                                        textDecoration: 'none'
                                    }
                                },
                                span: {
                                    color: 'black.100',
                                    lineHeight: '25px',
                                    fontSize: '16px',
                                    fontWeight: '300',
                                    fontFamily: 'body'
                                }
                            }}
                        >
                            <Box
                                ref={textRef}
                                noOfLines={isExpanded ? undefined : isIOS15 ? undefined : 3}
                                maxHeight={isExpanded ? undefined : isIOS15 ? '75px' : undefined}
                                overflow={isIOS15 ? 'hidden' : undefined}
                                dangerouslySetInnerHTML={{
                                    __html: category?.c_bottomCategoryDescription
                                }}
                            />
                        </Box>
                        {isTruncated && (
                            <Button
                                color="black.100"
                                _active={{color: 'black.100'}}
                                textDecoration="underline"
                                textUnderlineOffset="4px"
                                fontSize="16px"
                                marginTop="5px"
                                variant="link"
                                onClick={() => setIsExpanded(!isExpanded)}
                            >
                                {isExpanded
                                    ? intl.formatMessage({
                                          id: 'read.less',
                                          defaultMessage: 'Read Less'
                                      })
                                    : intl.formatMessage({
                                          id: 'read.more',
                                          defaultMessage: 'Read More'
                                      })}
                            </Button>
                        )}
                    </>
                )}
            </Box>
        </Box>
    )
}

PageFooter.displayName = 'PageFooter'

PageFooter.propTypes = {
    category: PropTypes.shape({
        c_bottomCategoryHeading: PropTypes.string,
        c_bottomCategoryDescription: PropTypes.string
    })
}

export default PageFooter
